/**
 * Local 4-digit PIN for fast offline unlocks.
 *
 * The PIN is never stored. What is persisted is a salted PBKDF2-SHA256 digest,
 * so a copy of the local IndexedDB does not reveal the code. Verification is
 * constant-time over the derived key.
 */
import type { LocalPinRecord } from '../types/user';

export const PIN_LENGTH = 4;
export const PIN_ITERATIONS = 120_000;
const SALT_BYTES = 16;
const KEY_BITS = 256;

export function isValidPin(pin: string): boolean {
  return new RegExp(`^\\d{${PIN_LENGTH}}$`).test(pin ?? '');
}

function toBase64(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

function fromBase64(value: string): Uint8Array<ArrayBuffer> {
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

async function derive(
  pin: string,
  salt: Uint8Array<ArrayBuffer>,
  iterations: number,
): Promise<string> {
  const key = await crypto.subtle.importKey('raw', new TextEncoder().encode(pin), 'PBKDF2', false, [
    'deriveBits',
  ]);
  const bits = await crypto.subtle.deriveBits(
    { name: 'PBKDF2', hash: 'SHA-256', salt, iterations },
    key,
    KEY_BITS,
  );
  return toBase64(new Uint8Array(bits));
}

/** Builds the persistable record for a PIN. Throws on a malformed PIN. */
export async function hashPin(userId: string, pin: string, now: number = Date.now()): Promise<LocalPinRecord> {
  if (!isValidPin(pin)) throw new Error(`PIN must be exactly ${PIN_LENGTH} digits`);
  const salt = crypto.getRandomValues(new Uint8Array(SALT_BYTES));
  return {
    userId,
    algorithm: 'PBKDF2-SHA256',
    iterations: PIN_ITERATIONS,
    salt: toBase64(salt),
    hash: await derive(pin, salt, PIN_ITERATIONS),
    updatedAt: new Date(now).toISOString(),
  };
}

/** Length-safe, constant-time string comparison. */
function timingSafeEqual(a: string, b: string): boolean {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i += 1) diff |= a.charCodeAt(i) ^ b.charCodeAt(i);
  return diff === 0;
}

export async function verifyPin(pin: string, record: LocalPinRecord | null | undefined): Promise<boolean> {
  if (!record || !isValidPin(pin)) return false;
  const candidate = await derive(pin, fromBase64(record.salt), record.iterations);
  return timingSafeEqual(candidate, record.hash);
}
